import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * Generate and preview a delivery note PDF in a new browser tab.
 * Accepts the raw API delivery note object plus storeInfo from the Redux store.
 */
export const generateAndPreviewDeliveryNotePDF = async (deliveryNote, storeInfo, t = (key) => key) => {
  try {
    if (!storeInfo) {
      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return false;
    }

    const templateHtml = await fetch('/deliverynotetemplates/deliverynote-template-en.html').then(r => r.text());

    const lineItems = deliveryNote.LineItems || [];
    const ProductRows = lineItems.map(p => `
      <tr>
        <td>${p.DisplayName || p.ProductName || p.Name || ''}</td>
        <td class="text-center">${p.Quantity || 0}</td>
        <td class="text-right">€${parseFloat(p.UnitPrice || p.Price || 0).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.TotalExcl || p.SubTotal || (Number(p.UnitPrice || p.Price || 0) * Number(p.Quantity || 0))).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.VATAmount || p.VatAmount || p.TaxAmount || p.TotalVAT || 0).toFixed(2)}</td>
        <td class="text-right">€${parseFloat(p.TotalIncl || p.TotalInclVAT || p.Total || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const rawDate = deliveryNote.IssueDate || deliveryNote.DeliveryNoteDate || deliveryNote.CreatedDate;
    const d = rawDate ? new Date(rawDate) : new Date();
    const formattedDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;

    let filledTemplate = templateHtml
      .replace(/{{CompanyName}}/g, storeInfo?.name || '')
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || '')
      .replace(/{{CompanyEmail}}/g, storeInfo?.email || '')
      .replace(/{{CompanyPhone}}/g, storeInfo?.phone || '')
      .replace(/{{CompanyAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCity}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || '')
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' '))
      .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || '')
      .replace(/{{CompanyBankAccount}}/g, storeInfo?.iban || '')
      .replace(/{{CustomerName}}/g, deliveryNote.CustomerName || '')
      .replace(/{{CustomerId}}/g, String(deliveryNote.CustomerId || ''))
      .replace(/{{CustomerVAT}}/g, deliveryNote.CustomerVAT || '')
      .replace(/{{CustomerNumber}}/g, String(deliveryNote.CustomerId || ''))
      .replace(/{{CustomerEmail}}/g, deliveryNote.CustomerEmail || '')
      .replace(/{{CustomerPhone}}/g, deliveryNote.CustomerPhone || deliveryNote.CustomerPhoneNumber || '')
      .replace(/{{CustomerAddress}}/g, deliveryNote.CustomerAddress || deliveryNote.CustomerStreet || '')
      .replace(/{{CustomerCity}}/g, deliveryNote.CustomerCity || '')
      .replace(/{{CustomerStreetAddress}}/g, deliveryNote.CustomerAddress || deliveryNote.CustomerStreet || '')
      .replace(/{{CustomerCityCountry}}/g, deliveryNote.CustomerCity || '')
      .replace(/{{DeliveryNoteNumber}}/g, deliveryNote.DeliveryNoteNumber || '')
      .replace(/{{DeliveryNoteDate}}/g, formattedDate)
      .replace(/{{Status}}/g, '')
      .replace(/status-{{Status}}/g, '')
      .replace(/{{LineItems}}/g, ProductRows)
      .replace(/{{{ProductRows}}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, String(deliveryNote.TotalQuantity || 0))
      .replace(/{{{DiscountRow}}}/g, '')
      .replace(/{{Subtotal}}/g, `€${parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2)}`)
      .replace(/{{TotalExcl}}/g, parseFloat(deliveryNote.TotalExcl || deliveryNote.SubTotal || 0).toFixed(2))
      .replace(/{{VATPercentage}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{VAT}}/g, String(deliveryNote.VATPercentage || 0))
      .replace(/{{TotalVAT}}/g, parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2))
      .replace(/{{VATAmount}}/g, `€${parseFloat(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || 0).toFixed(2)}`)
      .replace(/{{Discount}}/g, '€0.00')
      .replace(/{{TotalAmount}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{Total}}/g, `€${parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2)}`)
      .replace(/{{GrandTotal}}/g, parseFloat(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0).toFixed(2))
      .replace(/{{InvoicePaidAmount}}/g, parseFloat(deliveryNote.PaidAmount || deliveryNote.InvoicePaidAmount || 0).toFixed(2))
      .replace(/{{InvoiceDueAmount}}/g, parseFloat(deliveryNote.DueAmount || deliveryNote.InvoiceDueAmount || deliveryNote.TotalValue || deliveryNote.TotalAmount || 0).toFixed(2))
      .replace(/{{{PaymentMethodsSection}}}/g, '')
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1')
      .replace(/{{VATSection}}/g, '')
      .replace(/{{CustomText}}/g, deliveryNote.Notes || deliveryNote.CustomText || '')
      .replace(/{{CustomTextSection}}/g, '')
      .replace(/{{Notes}}/g, deliveryNote.Notes || '');

    // Force print colors and remove box-shadow for clean PDF capture
    filledTemplate = filledTemplate.replace(
      '</style>',
      `  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .delivery-note-container { box-shadow: none !important; margin: 0 !important; width: 794px !important; max-width: 794px !important; }
  </style>`
    );

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '794px';
    tempDiv.style.background = 'white';
    document.body.appendChild(tempDiv);

    // Brief pause for fonts/images to settle
    await new Promise(resolve => setTimeout(resolve, 300));

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 794,
      windowWidth: 794,
      backgroundColor: '#ffffff',
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0) {
      throw new Error('Invalid canvas generated');
    }

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight > pdfHeight ? pdfHeight : imgHeight);

    const pdfBlob = pdf.output('blob');
    window.open(URL.createObjectURL(pdfBlob), '_blank');

    return true;
  } catch (error) {
    console.error('❌ Delivery note PDF generation error:', error);
    toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF. Please try again.');
    return false;
  }
};

/**
 * Calculate due date based on invoice/delivery note date and payment term days
 */
export const calculateDueDate = (startDate, paymentTermDays = 15) => {
  const dueDate = new Date(startDate);
  dueDate.setDate(dueDate.getDate() + paymentTermDays);
  return dueDate;
};

/**
 * Format date to local string
 */
export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Format currency
 */
export const formatCurrency = (amount, currency = "EUR") => {
  if (isNaN(amount)) return "€0.00";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

/**
 * Generate delivery note PDF HTML from template
 */
export const generateDeliveryNotePdfHtml = (
  templateHtml,
  deliveryNoteData,
  companyData
) => {
  let html = templateHtml;

  // Replace delivery note placeholders
  html = html.replace(/{{DeliveryNoteNumber}}/g, deliveryNoteData.deliveryNoteNumber || "");
  html = html.replace(/{{DeliveryNoteDate}}/g, formatDate(deliveryNoteData.deliveryNoteDate));
  html = html.replace(/{{Status}}/g, deliveryNoteData.status || "Pending");

  // Replace company placeholders
  html = html.replace(/{{CompanyName}}/g, companyData.companyName || "");
  html = html.replace(/{{CompanyAddress}}/g, companyData.street || "");
  html = html.replace(/{{CompanyCity}}/g, `${companyData.postCode || ""} ${companyData.city || ""}`);
  html = html.replace(/{{CompanyCountry}}/g, companyData.country || "");
  html = html.replace(/{{CompanyVAT}}/g, companyData.vatNumber || "");
  html = html.replace(/{{CompanyPhone}}/g, companyData.phone || "");
  html = html.replace(/{{CompanyEmail}}/g, companyData.email || "");
  html = html.replace(/{{CompanyBankAccount}}/g, companyData.bankAccount || "");

  // Replace customer placeholders
  const customer = deliveryNoteData.customer || {};
  html = html.replace(/{{CustomerName}}/g, customer.Name || customer.CompanyName || "");
  html = html.replace(/{{CustomerAddress}}/g, customer.Street || "");
  html = html.replace(/{{CustomerCity}}/g, `${customer.PostCode || ""} ${customer.City || ""}`);
  html = html.replace(/{{CustomerCountry}}/g, customer.Country || "");
  html = html.replace(/{{CustomerVAT}}/g, customer.VATNumber || "");
  html = html.replace(/{{CustomerEmail}}/g, customer.Email || "");
  html = html.replace(/{{CustomerPhone}}/g, customer.PhoneNumber || "");

  // Generate line items HTML
  const lineItemsHtml = deliveryNoteData.products
    .map(
      (product, index) => `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">${index + 1}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">${product.ProductName || ""}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: center;">${product.Quantity || 0}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(product.Price || 0)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency((product.Quantity || 0) * (product.Price || 0))}</td>
    </tr>
  `
    )
    .join("");

  html = html.replace(/{{LineItems}}/g, lineItemsHtml);

  // Calculate totals
  const subtotal = deliveryNoteData.products.reduce(
    (sum, product) => sum + (product.Quantity || 0) * (product.Price || 0),
    0
  );

  const discount = deliveryNoteData.discountValue || 0;
  const vatPercentage = deliveryNoteData.vatPercentage || 0;
  
  let discountAmount = 0;
  if (deliveryNoteData.discountType === "percentage") {
    discountAmount = (subtotal * discount) / 100;
  } else {
    discountAmount = discount;
  }

  const totalBeforeVat = subtotal - discountAmount;
  const vatAmount = (totalBeforeVat * vatPercentage) / 100;
  const total = totalBeforeVat + vatAmount;

  // Calculate payment amounts
  const totalAmount = total;
  const paidAmount = Number(deliveryNoteData.invoicePaidAmount || 0);
  const dueAmount = total - paidAmount;

  // Build payment methods section for multiple payments
  let paymentMethodsSection = '';
  if (deliveryNoteData.paymentMethods && deliveryNoteData.paymentMethods.length > 0 && paidAmount > 0) {
    const paymentMethodsList = deliveryNoteData.paymentMethods
      .map(pm => `<div class="total-row"><span>${pm.name || pm.PaymentMethodName || 'Payment'}:</span><strong>€${Number(pm.amount || pm.Amount || 0).toFixed(2)}</strong></div>`)
      .join('');
    paymentMethodsSection = paymentMethodsList;
  } else if (paidAmount > 0 && deliveryNoteData.paymentMethod && deliveryNoteData.paymentMethod !== '') {
    paymentMethodsSection = `<div class="total-row"><span>Payment Method:</span><strong>${deliveryNoteData.paymentMethod}</strong></div>`;
  }

  // Replace totals
  html = html.replace(/{{Subtotal}}/g, formatCurrency(subtotal));
  html = html.replace(/{{Discount}}/g, formatCurrency(discountAmount));
  html = html.replace(/{{VATPercentage}}/g, vatPercentage.toFixed(0));
  html = html.replace(/{{VAT}}/g, vatPercentage.toFixed(0));
  html = html.replace(/{{VATAmount}}/g, formatCurrency(vatAmount));
  html = html.replace(/{{TotalVAT}}/g, vatAmount.toFixed(2));
  html = html.replace(/{{Total}}/g, formatCurrency(total));
  html = html.replace(/{{GrandTotal}}/g, total.toFixed(2));
  html = html.replace(/{{TotalAmount}}/g, totalAmount.toFixed(2));

  // Replace payment information
  html = html.replace(/{{{PaymentMethodsSection}}}/g, paymentMethodsSection);
  html = html.replace(/{{InvoicePaidAmount}}/g, paidAmount.toFixed(2));
  html = html.replace(/{{InvoiceDueAmount}}/g, dueAmount >= 0 ? dueAmount.toFixed(2) : '0.00');

  // Replace page numbering
  html = html.replace(/{{PageNumber}}/g, '1');
  html = html.replace(/{{TotalPages}}/g, '1');

  // Replace notes/custom text
  html = html.replace(/{{Notes}}/g, deliveryNoteData.notes || deliveryNoteData.customText || "");
  html = html.replace(/{{CustomText}}/g, deliveryNoteData.customText || deliveryNoteData.notes || "");
  html = html.replace(/{{CustomTextSection}}/g, deliveryNoteData.customText 
    ? `<div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-left: 4px solid #667eea; border-radius: 5px;"><strong style="color: #667eea;">Additional Notes:</strong><br><div style="margin-top: 8px; line-height: 1.6;">${deliveryNoteData.customText}</div></div>` 
    : '');

  return html;
};
